/*
Control Register Service

This service handles all API calls for control register with maker-checker workflow.
Following established patterns from PROJECT_CONVENTIONS.md:
- Uses axios with withCredentials for session authentication
- Standardized response format conversion
- Error handling with proper status codes
- Base URL configuration
*/

import apiClient from './apiService';
import { AxiosResponse } from 'axios';
import { statusBadgeCss } from '../utils/statusVariant';
import { GeographySelection } from '../components/common/GeographyPicker';

// Use shared apiClient
const apiService = apiClient;

// Types
export interface ControlRegister {
  id: number;
  entity_id: number;
  control_code: string;
  control_name: string;
  control_description?: string;
  category_id: number;
  category_name?: string;
  control_type?: string;
  is_active: number;
  is_approved: number;
  created_by?: number;
  created_on?: string;
  modified_by?: number;
  modified_on?: string;
  // UI-only helpers for workflow display
  status?: string;
  rejection_reason?: string;
  // Standard workflow fields (migration 123)
  workflow_state?: string;
  submitted_by?: number | null;
  submitted_on?: string | null;
  submitted_by_name?: string | null;
  approved_by?: number | null;
  approved_on?: string | null;
  approved_by_name?: string | null;
  approval_note?: string | null;
  rejected_by?: number | null;
  rejected_on?: string | null;
  rejected_by_name?: string | null;
  // Monitoring fields derived from CTR_control_score_history (folded in from
  // the dropped Monitoring tab). Net score = effectiveness * existence (1-25).
  // All optional — controls with no test history come back as null.
  latest_effectiveness?: number | null;
  latest_existence?: number | null;
  latest_net?: number | null;
  prev_net?: number | null;
  avg3m_net?: number | null;
  avg3m_effectiveness?: number | null;
  avg3m_existence?: number | null;
  checks_3m?: number | null;
  // 3-month-average dimensions, rounded — drives the Insights matrix cell and
  // the unified signal. Null when fewer than 2 checks in the window (UNKNOWN).
  display_effectiveness?: number | null;
  display_existence?: number | null;
  display_net_score?: number | null;
  net_deficiency?: number | null;
  last_check?: string | null;
  trend?: 'UP' | 'DOWN' | 'STABLE' | 'UNKNOWN';
  signal?: 'HEALTHY' | 'WARNING' | 'BREACH' | 'UNKNOWN';
  // Downstream link counts (aggregated in the list query, no N+1).
  risk_count?: number | null;
  implementation_count?: number | null;
  // System-generated code (e.g. "CTR-1") — null for drafts, set on submit
  code?: string | null;
}

export interface ControlCategory {
  id: number;
  category_code: string;
  category_name: string;
  description?: string;
}

export interface ControlQueryParams {
  status?: 'ALL' | 'DRAFT' | 'PENDING_APPROVAL' | 'APPROVED' | 'REJECTED' | 'ACTIVE' | 'INACTIVE';
  search?: string;
  /** Page size per request while paging. The method still returns EVERY row. */
  pageSize?: number;
}

export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
  total?: number;
}

export interface ControlStatistics {
  total: number;
  draft: number;
  pending_approval: number;
  approved: number;
  rejected: number;
  active: number;
  inactive: number;
}

export interface StepLinkedControl {
  id: number;
  code: string;
  name: string;
  description?: string | null;
  type?: string | null;
  category_id?: number | null;
  category_name?: string | null;
  map_id?: number;
}

export interface ControlLinkedRisk {
  id: number;
  code: string;
  name: string;
  description?: string | null;
}

export interface ControlImplementationStep {
  id: number;
  control_id: number;
  sequence_number: number;
  implementation_type: 'SOP' | 'IT_APPLICATION' | 'IT_ASSET';
  target_id: number;
  target_label?: string | null;
  target_subtitle?: string | null;
  remarks?: string | null;
}

export interface ControlScoreHistoryRow {
  id: number;
  scored_on: string;
  effectiveness_score?: number | null;
  efficiency_score?: number | null;
  existence_score?: number | null;
  net_control_score?: number | null;
  net_control_deficiency?: number | null;
  // 'adhoc' (recorded directly from the Control Register) vs 'control_test'
  // (came from an approved CAT program). Drives the Source column + whether the
  // row can be deleted from the Test Results tab.
  source?: string | null;
  location_name?: string | null;
  test_instance_id?: number | null;
  test_name?: string | null;
  start_date?: string | null;
  end_date?: string | null;
  maker_name?: string | null;
  remarks?: string | null;
}

export interface PaginationMeta {
  page: number;
  limit: number;
  total: number;
  pages: number;
}

export interface ScoreHistoryPage {
  rows: ControlScoreHistoryRow[];
  total: number;
  page: number;
  pages: number;
}

export interface AdhocControlScorePayload {
  effectiveness_score: number;
  existence_score: number;
  date_of_testing?: string;
  remarks?: string;
  geography: GeographySelection;
}

class ControlRegisterService {

  /**
   * Score history for a control, newest first — server-side paginated and
   * searchable (geography / test program / source / maker / remarks). Used by
   * the Test Results tab of ControlConfigModal.
   */
  async getScoreHistory(
    controlId: number,
    params: { page?: number; limit?: number; search?: string } = {},
  ): Promise<ScoreHistoryPage> {
    const resp: AxiosResponse<ApiResponse<ControlScoreHistoryRow[]> & { meta?: PaginationMeta }> =
      await apiService.get(`/api/v1/ctr/controls/${controlId}/score-history`, {
        params: {
          page: params.page ?? 1,
          limit: params.limit ?? 20,
          search: params.search?.trim() || undefined,
        },
      });
    if (resp.data.success && resp.data.data) {
      const m = resp.data.meta;
      return {
        rows: resp.data.data,
        total: m?.total ?? resp.data.data.length,
        page: m?.page ?? 1,
        pages: m?.pages ?? 1,
      };
    }
    throw new Error(resp.data.error || 'Failed to fetch score history');
  }

  /**
   * Record an ad-hoc control score directly (no CAT program, no checker).
   * Gated server-side by the Control Register add/edit permission. The
   * geography picker may resolve to several locations; the backend writes one
   * score row per location and returns how many it created.
   */
  async recordAdhocScore(controlId: number, payload: AdhocControlScorePayload): Promise<number> {
    const resp: AxiosResponse<ApiResponse<unknown> & { created?: number }> = await apiService.post(
      `/api/v1/ctr/controls/${controlId}/adhoc-score`, payload
    );
    if (resp.data.success) return resp.data.created ?? 0;
    throw new Error(resp.data.error || 'Failed to record score');
  }

  /**
   * Soft-delete an ad-hoc score row from the Test Results tab. The server
   * rejects deletion of program (control_test) scores.
   */
  async deleteScoreHistory(controlId: number, scoreId: number): Promise<void> {
    const resp: AxiosResponse<ApiResponse<unknown>> = await apiService.delete(
      `/api/v1/ctr/controls/${controlId}/score-history/${scoreId}`
    );
    if (!resp.data.success) throw new Error(resp.data.error || 'Failed to delete score');
  }

  /**
   * Get all controls with optional filtering
   */
  /**
   * Fetch EVERY control matching the params, paging until the server is
   * exhausted.
   *
   * This used to send no `page` and no `limit`, so the server applied its
   * default of 20 and the caller silently received at most twenty controls —
   * and because the method returns a bare array, `meta.total` was structurally
   * unreachable, so nothing could even detect the truncation. Insights computed
   * every figure on those twenty rows and presented them as the whole picture;
   * the register paginated client-side over them and reported the count it had
   * loaded as the total. Below 21 controls the defect was invisible.
   *
   * Callers that want one page should call the API directly with page/limit and
   * read `meta`. This method's contract is "all of them".
   */
  async getAll(params?: ControlQueryParams): Promise<ControlRegister[]> {
    const MAX_PAGE = 100;          // server clamps `limit` to 100
    const HARD_STOP = 500;         // pages; a guard against a server paging bug
    const limit = Math.min(params?.pageSize ?? MAX_PAGE, MAX_PAGE);

    try {
      const all: ControlRegister[] = [];
      let page = 1;

      for (;;) {
        const queryParams = new URLSearchParams();
        if (params?.status && params.status !== 'ALL') {
          queryParams.append('status', params.status);
        }
        if (params?.search) {
          queryParams.append('search', params.search);
        }
        queryParams.append('page', String(page));
        queryParams.append('limit', String(limit));

        const url = `/api/v1/ctr/controls?${queryParams.toString()}`;
        const response: AxiosResponse<ApiResponse<ControlRegister[]>> =
          await apiService.get(url);

        if (!response.data.success || !response.data.data) {
          throw new Error(response.data.error || 'Failed to fetch controls');
        }

        const batch = response.data.data;
        all.push(...batch);

        // Stop on a short page, on an explicit total, or at the hard stop.
        const total = (response.data as any)?.meta?.total;
        const done =
          batch.length < limit ||
          (typeof total === 'number' && all.length >= total) ||
          page >= HARD_STOP;
        if (done) {
          if (page >= HARD_STOP) {
            console.warn(
              `controlRegisterService.getAll stopped at ${HARD_STOP} pages; ` +
              'the result may be incomplete.',
            );
          }
          break;
        }
        page += 1;
      }

      return all;
    } catch (error: any) {
      console.error('Error fetching controls:', error);
      // Handle 401 Unauthorized - redirect to login
      if (error.response?.status === 401) {
        window.location.href = '/login';
      }
      throw error;
    }
  }


  /**
   * Get a single control by ID
   */
  async getById(controlId: number): Promise<ControlRegister> {
    try {
      const response: AxiosResponse<ApiResponse<ControlRegister>> = 
        await apiService.get(`/api/v1/ctr/controls/${controlId}`);

      if (response.data.success && response.data.data) {
        return response.data.data;
      } else {
        throw new Error(response.data.error || 'Failed to fetch control');
      }
    } catch (error: any) {
      console.error(`Error fetching control ${controlId}:`, error);
      if (error.response?.status === 401) {
        window.location.href = '/login';
      }
      throw error;
    }
  }

  /**
   * Create a new control in DRAFT status
   */
  async create(controlData: Partial<ControlRegister>, action: 'draft' | 'submit' | 'approve' = 'draft'): Promise<ControlRegister> {
    try {
      const response: AxiosResponse<ApiResponse<ControlRegister>> =
        await apiService.post(`/api/v1/ctr/controls?action=${action}`, controlData);

      if (response.data.success && response.data.data) {
        return response.data.data;
      } else {
        throw new Error(response.data.error || 'Failed to create control');
      }
    } catch (error: any) {
      console.error('Error creating control:', error);
      if (error.response?.status === 401) {
        window.location.href = '/login';
      }
      throw error;
    }
  }

  /**
   * Update an existing control (only DRAFT and REJECTED can be updated)
   */
  async update(controlId: number, updateData: Partial<ControlRegister>): Promise<ControlRegister> {
    try {
      const response: AxiosResponse<ApiResponse<ControlRegister>> = 
        await apiService.put(`/api/v1/ctr/controls/${controlId}`, updateData);

      if (response.data.success && response.data.data) {
        return response.data.data;
      } else {
        throw new Error(response.data.error || 'Failed to update control');
      }
    } catch (error: any) {
      console.error(`Error updating control ${controlId}:`, error);
      if (error.response?.status === 401) {
        window.location.href = '/login';
      }
      throw error;
    }
  }

  async submit(controlId: number): Promise<void> {
    try {
      await apiService.post(`/api/v1/ctr/controls/${controlId}/submit`);
    } catch (error: any) {
      const msg = error.response?.data?.detail || error.response?.data?.error || 'Failed to submit control';
      throw new Error(msg);
    }
  }

  async approve(controlId: number, note?: string): Promise<void> {
    try {
      await apiService.post(`/api/v1/ctr/controls/${controlId}/approve`, { note: note ?? null });
    } catch (error: any) {
      const msg = error.response?.data?.detail || error.response?.data?.error || 'Failed to approve control';
      throw new Error(msg);
    }
  }

  async reject(controlId: number, reason: string): Promise<void> {
    try {
      await apiService.post(`/api/v1/ctr/controls/${controlId}/reject`, { reason });
    } catch (error: any) {
      const msg = error.response?.data?.detail || error.response?.data?.error || 'Failed to reject control';
      throw new Error(msg);
    }
  }

  async reopen(controlId: number): Promise<void> {
    try {
      await apiService.post(`/api/v1/ctr/controls/${controlId}/reopen`);
    } catch (error: any) {
      const msg = error.response?.data?.detail || error.response?.data?.error || 'Failed to reopen control';
      throw new Error(msg);
    }
  }

  /**
   * Delete a control (hard delete - only DRAFT and REJECTED can be deleted)
   */
  async delete(controlId: number, hardDelete: boolean = true): Promise<void> {
    try {
      const url = `/api/v1/ctr/controls/${controlId}${hardDelete ? '?hard_delete=true' : ''}`;
      const response: AxiosResponse<ApiResponse<void>> = 
        await apiService.delete(url);

      if (!response.data.success) {
        throw new Error(response.data.error || 'Failed to delete control');
      }
    } catch (error: any) {
      console.error(`Error deleting control ${controlId}:`, error);
      if (error.response?.status === 401) {
        window.location.href = '/login';
      }
      throw error;
    }
  }

  /**
   * Toggle the active status of a control (activate/inactivate - only APPROVED controls)
   */
  async toggleStatus(controlId: number): Promise<ControlRegister> {
    try {
      const response: AxiosResponse<ApiResponse<ControlRegister>> = 
        await apiService.patch(`/api/v1/ctr/controls/${controlId}/toggle`);

      if (response.data.success && response.data.data) {
        return response.data.data;
      } else {
        throw new Error(response.data.error || 'Failed to toggle control status');
      }
    } catch (error: any) {
      console.error(`Error toggling control ${controlId} status:`, error);
      if (error.response?.status === 401) {
        window.location.href = '/login';
      }
      throw error;
    }
  }

  /**
   * Get statistics for control register
   */
  async getStatistics(): Promise<ControlStatistics> {
    try {
      const response: AxiosResponse<ApiResponse<ControlStatistics>> = 
        await apiService.get('/api/v1/ctr/controls/statistics');

      if (response.data.success && response.data.data) {
        return response.data.data;
      } else {
        throw new Error(response.data.error || 'Failed to fetch statistics');
      }
    } catch (error: any) {
      console.error('Error fetching control register statistics:', error);
      if (error.response?.status === 401) {
        window.location.href = '/login';
      }
      throw error;
    }
  }

  /**
   * Get all active control categories for dropdown
   */
  async getActiveCategories(): Promise<ControlCategory[]> {
    // Use the unified dropdown endpoint so only ACTIVE + APPROVED categories
    // (green-dot) appear — /categories?status=ACTIVE filters active only and
    // would leak draft/submitted categories into the picker.
    const response = await apiService.get('/api/v1/ctr/categories/dropdown');
    const items = response.data?.items ?? [];
    return items.map((it: { id: number; name: string; code?: string }) => ({
      id: it.id,
      category_code: it.code ?? '',
      category_name: it.name,
    }));
  }

  /** Approved + active controls only, enriched for the risk-link dual-list. */
  async getDropdown(): Promise<{ id: number; name: string; code?: string | null; control_type?: string | null; category_name?: string | null }[]> {
    const response = await apiService.get('/api/v1/ctr/controls/dropdown');
    return response.data?.items ?? [];
  }

  /**
   * Validate control data before submission
   */
  validateData(controlData: Partial<ControlRegister>, isUpdate: boolean = false): { valid: boolean; errors: string[] } {
    const errors: string[] = [];

    // Required field validation (for create). control_code is optional —
    // auto-generated (CTR-N) on submit/approve when omitted.
    if (!isUpdate) {
      if (!controlData.control_name || controlData.control_name.trim() === '') {
        errors.push('Control name is required');
      }
      if (!controlData.category_id) {
        errors.push('Control category is required');
      }
    }

    // Format validation for control code (only when supplied)
    if (controlData.control_code) {
      const codePattern = /^[A-Z0-9-]+$/;
      if (!codePattern.test(controlData.control_code)) {
        errors.push('Control code must contain only uppercase letters, numbers, and hyphens');
      }
    }

    return {
      valid: errors.length === 0,
      errors
    };
  }

  /**
   * Get status badge color for UI display
   */
  getStatusBadgeClass(status: string): string {
    return statusBadgeCss(status);
  }

  /**
   * Get status display text
   */
  getStatusDisplayText(status: string): string {
    const statusMap: Record<string, string> = {
      'DRAFT': 'Draft',
      'PENDING_APPROVAL': 'Pending Approval',
      'APPROVED': 'Approved',
      'REJECTED': 'Rejected'
    };
    return statusMap[status] || status;
  }

  /**
   * Display text for the approval workflow_state (migration 123).
   * The legacy `status` column is unpopulated — workflow_state is the
   * live source of truth, also shown by the ApprovalDot.
   */
  getWorkflowStatusText(workflowState?: string | null): string {
    const map: Record<string, string> = {
      draft: 'Draft',
      submitted: 'Submitted',
      approved: 'Approved',
      rejected: 'Rejected'
    };
    const wf = (workflowState || 'draft').toLowerCase();
    return map[wf] || 'Draft';
  }

  /**
   * Check if control can be edited
   */
  canEdit(control: ControlRegister): boolean {
    return control.status === 'DRAFT' || control.status === 'REJECTED';
  }

  /**
   * Check if control can be submitted
   */
  canSubmit(control: ControlRegister): boolean {
    const wf = control.workflow_state ?? 'draft';
    return wf === 'draft' || wf === 'rejected';
  }

  canDelete(control: ControlRegister): boolean {
    const wf = control.workflow_state ?? 'draft';
    return wf === 'draft' || wf === 'rejected';
  }

  canToggle(control: ControlRegister): boolean {
    // Frozen when approved: deactivate is only allowed before approval
    // (reopen to draft first). Matches the app-wide approved-lock rule.
    const wf = control.workflow_state ?? 'draft';
    return wf !== 'approved';
  }

  /**
   * Health check for the control register module
   */
  async healthCheck(): Promise<{ status: string; timestamp: string }> {
    try {
      const response = await apiService.get('/api/v1/ctr/controls/health');
      return response.data;
    } catch (error) {
      console.error('Control register health check failed:', error);
      throw error;
    }
  }

  // ───────────────────────────────────────────────────────────────────────
  // SOP step ↔ Control mapping (ctr_procedure_maps from the step side)
  // ───────────────────────────────────────────────────────────────────────

  async listStepControls(stepId: number): Promise<StepLinkedControl[]> {
    const resp: AxiosResponse<ApiResponse<StepLinkedControl[]>> =
      await apiService.get(`/api/v1/ctr/process-steps/${stepId}/controls`);
    if (resp.data.success && resp.data.data) return resp.data.data;
    throw new Error(resp.data.error || 'Failed to fetch step controls');
  }

  async linkControlToStep(stepId: number, controlId: number, remarks?: string): Promise<{ id: number }> {
    const resp: AxiosResponse<ApiResponse<{ id: number }>> =
      await apiService.post(`/api/v1/ctr/process-steps/${stepId}/controls`, { control_id: controlId, remarks });
    if (resp.data.success && resp.data.data) return resp.data.data;
    throw new Error(resp.data.error || 'Failed to link control');
  }

  async unlinkControlFromStep(stepId: number, controlId: number): Promise<void> {
    const resp: AxiosResponse<ApiResponse<unknown>> =
      await apiService.delete(`/api/v1/ctr/process-steps/${stepId}/controls/${controlId}`);
    if (!resp.data.success) throw new Error(resp.data.error || 'Failed to unlink control');
  }

  // ───────────────────────────────────────────────────────────────────────
  // Risks linked to a control (rsk_control_maps)
  // ───────────────────────────────────────────────────────────────────────

  async getControlRisks(controlId: number): Promise<ControlLinkedRisk[]> {
    const resp: AxiosResponse<ApiResponse<ControlLinkedRisk[]>> =
      await apiService.get(`/api/v1/ctr/controls/${controlId}/risks`);
    if (resp.data.success && resp.data.data) return resp.data.data;
    throw new Error(resp.data.error || 'Failed to fetch control risks');
  }

  // ───────────────────────────────────────────────────────────────────────
  // Implementations of a control (ctr_implementation_steps — downstream
  // polymorphic links: SOP / IT_APPLICATION / IT_ASSET).
  // ───────────────────────────────────────────────────────────────────────

  async getControlImplementations(controlId: number): Promise<ControlImplementationStep[]> {
    const resp: AxiosResponse<ApiResponse<ControlImplementationStep[]>> =
      await apiService.get(`/api/v1/ctr/controls/${controlId}/implementations`);
    if (resp.data.success && resp.data.data) return resp.data.data;
    throw new Error(resp.data.error || 'Failed to fetch control implementations');
  }
}

// Export singleton instance
const controlRegisterService = new ControlRegisterService();
export default controlRegisterService;
